import React from 'react';
import { AdsResponse, CurrencyResponse, isActiveAdStatus } from '../../../../types/api/ads';
import { renderRow } from '../../../basic/InfoSection';
import AdsInfoHeader from './AdsInfoHeader';
import { HeartIcon, EyeIcon, CommentIcon, ClockIcon, CopyIcon, ChevronRightIcon } from '@primer/octicons-react';
import { Text, Button, Stack, Label } from '@primer/react';
import { AddressResponse } from '../../../../types/api/common';

interface AdsCardProps {
    ads: AdsResponse;
    onOpenClick?: () => void;
}

const formatPrice = (price: number, currency: CurrencyResponse): string => {
    return `${price.toLocaleString()} ${currency.symbol}`;
};

const formatDate = (dateString: string): { date: string; time: string } => {
    const date = new Date(dateString);
    return {
        date: date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit'
        }),
        time: date.toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit'
        })
    };
};

const formatAddress = (address: AddressResponse): string => {
    const parts: string[] = [];

    if (address.postalCode) parts.push(address.postalCode);
    if (address.city?.title) parts.push(`г. ${address.city.title}`);
    if (address.street) parts.push(`${address.street} ул.`);
    if (address.house) parts.push(`д. ${address.house}`);
    if (address.building) parts.push(`кв.${address.building}`);

    return parts.join(', ');
};

const AdsCard: React.FC<AdsCardProps> = ({
    ads,
    onOpenClick
}) => {
    const { date, time } = formatDate(ads.publishedAt);
    const fullAddress = ads.address && formatAddress(ads.address);

    const handleCardClick = (): void => {
        onOpenClick?.();
    };

    return (
        <div
            className='bg-white border border-border rounded-lg'>
            {/* Header с изображением и основной информацией */}
            <AdsInfoHeader
                className='mt-4 ml-3 mr-3 mb-4'
                ads={ads}
                imageSize={72}
                nameFontSize={16}
            />
            <div className="space-y-4 bg-header pt-4 pl-3 pr-3 pb-4 border-b border-t border-border">
                {/* Описание */}
                {ads.description && renderRow(
                    'Описание',
                    ads.description,
                    false,
                    Text,
                    CopyIcon,
                    '',
                    'text-[14px] text-aloix-grey truncate'
                )}

                {/* Адрес */}
                {renderRow(
                    'Адрес',
                    fullAddress,
                    true,
                    Text,
                    CopyIcon
                )}
            </div>

            {/* Footer со статистикой*/}
            <Stack direction="horizontal" align="center" justify="space-between" className='mt-4 ml-3 mr-3 mb-4'>
                <Stack direction="horizontal" align="center">
                    <Stack direction="horizontal" align="center">
                        <Stack direction="horizontal" align="center" gap="condensed">
                            <ClockIcon size={16} />
                            <Text className='text-[14px] text-aloix-grey ml-1'>{date} в {time}</Text>
                        </Stack>
                        <Stack direction="horizontal" align="center" gap="condensed">
                            <EyeIcon size={16} />
                            <Stack direction="horizontal" align="center" gap="none">
                                <Text className='text-[14px] text-aloix-black'>{ads.viewsCount}</Text>
                                <Text className='text-[14px] text-aloix-grey ml-1'>({ads.todayViewsCount} сегодня)</Text>
                            </Stack>
                        </Stack>
                        <Stack direction="horizontal" align="center" gap="condensed">
                            <CommentIcon size={16} />
                            <Text className='text-[14px] text-aloix-black'>{ads.commentsCount}</Text>
                        </Stack>
                        <Stack direction="horizontal" align="center" gap="condensed">
                            <HeartIcon size={16} />
                            <Text className='text-[14px] text-aloix-black'>{ads.favouritesCount}</Text>
                        </Stack>
                    </Stack>
                </Stack>

                <Button
                    variant="default"
                    size="small"
                    onClick={handleCardClick}
                    trailingVisual={ChevronRightIcon}
                >
                    Перейти
                </Button>
            </Stack>
        </div>
    );
};

export default AdsCard